import { FC, useCallback, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import { atom, useAtom } from 'jotai'
import { debounce, uniqueId } from 'lodash-es'

import {
  Button,
  Cluster,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Icon,
  Separator,
  Stack,
  TextArea,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useAuth } from '@/features/auth'
import { DocumentUpload } from '@/features/documents'
import { useLocalisation } from '@/utils/localisation'

import { MessageContentProps, MessagesCollectionStore } from './MessagesCollectionStore'
import { ActivityFeedFormSchema, ActivityFeedFormSchemaType } from '../api/schema'
import {
  useV1ShipmentEntityFeedActivityCreate,
  useV2ShipmentEntityFeedActivityCreate,
} from '../api/useEntityFeedActivityCreate'

type AutosaveStatus = 'saved' | 'empty' | 'tooLong' | 'writing'

export type ActivityFeedFormProps = {
  id: string
  v2Flow: boolean
}

const dialogState = atom(false)

const maxMessageLength = 5000
const debounceDelay = 500

const messageStore = new MessagesCollectionStore()

const saveMessageToStorage = (message: string, entityID: string, collectionID: string): MessageContentProps<string> => {
  return messageStore.saveMessageToCollection({
    messageID: entityID,
    message,
    collectionID: collectionID,
  })
}

const deleteMessageFromStorage = (entityID: string, collectionID: string): void => {
  messageStore.removeMessageFromCollection({
    collectionID: collectionID,
    messageID: entityID,
  })
}

export const ActivityFeedForm: FC<ActivityFeedFormProps> = ({ id, v2Flow }) => {
  const { translate, translateDate } = useLocalisation()
  const { user } = useAuth()
  const createForV1ShipmentMutation = useV1ShipmentEntityFeedActivityCreate()
  const createForV2ShipmentMutation = useV2ShipmentEntityFeedActivityCreate()

  const userID = user?._id
  const messageStoreCollectionID = userID ?? uniqueId('collection_')

  const storedMessage = messageStore.getMessage({
    collectionID: messageStoreCollectionID,
    messageID: id,
  })

  const toaster = useToaster()
  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(ActivityFeedFormSchema),
    defaultValues: {
      content: storedMessage?.content ?? '',
    },
  })
  const [open, setOpen] = useAtom(dialogState)

  const getAutoSaveText = useCallback(
    (saveTimestamp: number | null | undefined): string => {
      if (!saveTimestamp) {
        return ''
      }
      const autosaveText = translate('feed.form.fields.message.draft.saveTimestamp')
      const autoSaveFormattedDate = translateDate('activity_feed_autosave_date', new Date(saveTimestamp))
      return `${autosaveText} ${autoSaveFormattedDate}`
    },
    [translate, translateDate]
  )

  const [status, setStatus] = useState<AutosaveStatus>(storedMessage?.timestamp ? 'saved' : 'empty')

  const translatedStatus: Record<AutosaveStatus, () => string> = {
    empty: (): string => '',
    tooLong: (): string => translate('feed.form.fields.message.draft.tooLong'),
    writing: (): string => translate('feed.form.fields.message.draft.writing'),
    saved: (): string => getAutoSaveText(storedMessage?.timestamp),
  }

  const saveMessage = useCallback(
    (newMessage: string): void => {
      saveMessageToStorage(newMessage, id, messageStoreCollectionID)
      setStatus('saved')
    },
    [messageStoreCollectionID, id]
  )

  const deleteMessage = useCallback(() => {
    deleteMessageFromStorage(id, messageStoreCollectionID)
    setStatus('empty')
  }, [id, messageStoreCollectionID])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveMessageWithDebounce = useCallback(
    debounce((newMessage: string) => {
      if (newMessage.length === 0) {
        deleteMessage()
      } else {
        saveMessage(newMessage)
      }
    }, debounceDelay),
    [translate, deleteMessage, saveMessage]
  )

  const onChangeMessage = (value: string): void => {
    if (value.length < maxMessageLength) {
      setStatus('writing')
      saveMessageWithDebounce(value)
    } else {
      setStatus('tooLong')
    }
  }

  const onSubmit: SubmitHandler<ActivityFeedFormSchemaType> = async (data) => {
    if (v2Flow) {
      await createForV2ShipmentMutation.mutateAsync({
        entityId: id,
        user,
        data,
      })
    } else {
      await createForV1ShipmentMutation.mutateAsync({
        entityId: id,
        user,
        data,
      })
    }
    toaster({
      title: translate('feed.form.notifications.sent'),
      variant: 'positive',
    })
    deleteMessage()
    reset()
  }

  return (
    <Stack gap={8}>
      <Typography variant="body-bold">{translate('feed.form.title')}</Typography>
      <Stack gap={16}>
        <TextArea
          {...register('content')}
          placeholder={translate('feed.form.fields.message.placeholder')}
          state={errors.content ? 'negative' : undefined}
          onChange={(e) => onChangeMessage(e.target.value)}
          disabled={createForV1ShipmentMutation.isPending || createForV2ShipmentMutation.isPending}
          actions={
            <Cluster gap={8} x="start" y="baseline" fullWidth>
              <Typography variant="tiny" color="muted">
                {translatedStatus[status]()}
              </Typography>
              <Cluster gap={8} y="center">
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger>
                    <Icon size="sm" name="Attach" />
                  </DialogTrigger>
                  <DialogContent>
                    <DialogTitle>{translate('feed.form.actions.attach')}</DialogTitle>
                    <DocumentUpload id={id} onComplete={() => setOpen(false)} />
                  </DialogContent>
                </Dialog>
                <Stack gap={8}>
                  <Separator orientation="vertical" />
                </Stack>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSubmit(onSubmit)}
                  disabled={createForV1ShipmentMutation.isPending || createForV2ShipmentMutation.isPending}
                >
                  <Cluster gap={4} y="center">
                    {translate('feed.form.actions.send')} <Icon size="xs" name="ArrowRight" />
                  </Cluster>
                </Button>
              </Cluster>
            </Cluster>
          }
        />
      </Stack>
    </Stack>
  )
}
